import { useCallback, useEffect, useState } from "react";

import { getCertifiedCoursesByUser } from "../helpers/tracking.helper";
import useContactRelation from "./contactRelation.hook";

/**
 * Cursos con CERTIFICADO — el usuario aprobó su examen final. Los
 * consume la vista /logros para pintar únicamente los certificados
 * descargables, y home/mylearning para diferenciar "listo para
 * examen" de "certificado obtenido".
 *
 * Distinto de `useCompletedCourses`, que devuelve los cursos con
 * LECCIONES vistas (Trazabilidad). Un usuario puede haber terminado
 * las lecciones sin haber aprobado el examen — para él no hay
 * certificado que descargar.
 *
 * Refresco: se relanza cuando la ventana vuelve a foco. Cubre el
 * caso "acabo de aprobar un examen y vuelvo al home" sin exigir
 * recarga manual — el hook detecta el focus, refetchea y las cards
 * del home se recomponen con el certificado ya presente.
 *
 * Sin sesión devuelve `[]` sin pegarle a la red.
 */
const useCertifiedCourses = () => {
  const { token, userLocal } = useContactRelation();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchNow = useCallback(async () => {
    if (!token || !userLocal?.id) {
      setIsLoading(false);
      return;
    }
    const response = await getCertifiedCoursesByUser(userLocal.id);
    setData(response);
    setIsLoading(false);
  }, [token, userLocal?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      /* Reportado en /logros: "sale primero que no hay logros y
         después sí se listan". Causa: `useContactRelation` inicializa
         `token` síncrono desde localStorage, pero `userLocal` empieza
         `undefined` y lo rellena en un useEffect propio. Sin este
         guard, el primer render con `token` presente y `userLocal`
         vacío caía al `[]` y ponía `isLoading = false` → la vista
         pintaba EmptyState durante el hueco entre renders. Ahora:
           · sin token → confirmado anónimo, data vacía, deja de cargar.
           · token pero sin userLocal.id → sigue cargando (skeleton).
           · ambos listos → fetch real. */
      if (!token) {
        if (!cancelled) {
          setData([]);
          setIsLoading(false);
        }
        return;
      }
      if (!userLocal?.id) {
        return;
      }
      const response = await getCertifiedCoursesByUser(userLocal.id);
      if (!cancelled) {
        setData(response);
        setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, userLocal?.id]);

  useEffect(() => {
    if (!token || !userLocal?.id) return undefined;
    const onFocus = () => {
      fetchNow();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchNow, token, userLocal?.id]);

  return { data, isLoading, refresh: fetchNow };
};

export default useCertifiedCourses;
